import React from 'react';
import { FaSearch, FaPlus, FaUsers } from 'react-icons/fa';
import { useModal } from '../../context/ModalContext';
import { getLeadTemperature } from '../../utils/uniboxComposer';

function cleanPhone(str) {
  if (!str) return '';
  return String(str).replace(/@s\.whatsapp\.net|@g\.us|@lid/g, '').replace(/\D/g, '');
}

function formatPhone(phone) {
  const clean = cleanPhone(phone);
  if (!clean) return '';
  if (clean.length >= 10) {
    const cc = clean.slice(0, clean.length - 10);
    const num = clean.slice(-10);
    return cc ? `+${cc} ${num.slice(0, 5)} ${num.slice(5)}` : num;
  }
  return clean;
}

function formatRelativeTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) {
    return d.toLocaleDateString([], { weekday: 'short' });
  }
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
}

// Pipeline stages are now passed as a prop (pipelineStages)

export default function UniboxSidebar({
  sessions,
  sessionId,
  setSessionId,
  contacts,
  selectedContact,
  onSelect,
  searchText,
  setSearchText,
  contactFilter,
  setContactFilter,
  stageFilter,
  setStageFilter,
  getDisplayName,
  pipelineStages = [],
  className,
}) {
  const modal = useModal();

  const handleNewChat = async () => {
    const newNumber = await modal.prompt('Enter WhatsApp number (with country code):', {
      placeholder: 'e.g. 919876543210',
    });
    if (!newNumber) return;
    if (!/^\d{7,15}$/.test(newNumber)) {
      modal.alert('Invalid number. Please enter 7–15 digits with country code.');
      return;
    }
    onSelect({
      phone: newNumber,
      jid: newNumber + '@s.whatsapp.net',
      isChat: true,
      isLead: false,
      name: '',
    });
  };

  return (
    <div
      className={`w-full md:w-[300px] h-full bg-surface-card border-r border-surface-border flex flex-col flex-shrink-0 ${className || ''}`}
    >
      {/* Session dropdown */}
      <div className="px-4 md:px-3 py-3 border-b border-surface-border">
        <label className="text-xs font-semibold text-ink-muted mb-1 block">Logged in WhatsApp:</label>
        <select
          value={sessionId || ''}
          onChange={(e) => setSessionId(e.target.value)}
          className="input text-sm"
        >
          {sessions && sessions.map((s) => {
            const num = (s.number || s.sessionId || '').split(':')[0];
            const isCloud = s.transportType === 'walytic_cloud';
            const isBrowser = s.transportType === 'walytic_browser';
            const prefix = isCloud ? '☁ ' : isBrowser ? '🖥 ' : '';
            const suffix = isCloud ? ' (Cloud)' : isBrowser ? ' (Browser)' : '';
            return (
              <option key={s.sessionId} value={s.sessionId}>
                {prefix}{formatPhone(num)}{suffix}
              </option>
            );
          })}
        </select>
      </div>

      {/* New Chat button */}
      <div className="px-4 md:px-3 pt-3">
        <button
          className="btn-primary btn-sm w-full flex items-center justify-center gap-1.5"
          onClick={handleNewChat}
        >
          <FaPlus size={11} />
          Start New Chat
        </button>
      </div>

      {/* Filters */}
      <div className="px-4 md:px-3 py-2 border-b border-surface-border">
        <div className="flex gap-1">
          {['all', 'leads', 'other'].map((filter) => (
            <button
              key={filter}
              onClick={() => setContactFilter(filter)}
              className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                contactFilter === filter
                  ? 'bg-brand text-white'
                  : 'bg-surface-bg text-ink-secondary hover:bg-surface-border'
              }`}
            >
              {filter === 'other' ? 'Non-leads' : filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>

        {contactFilter !== 'other' && (
          <select
            value={stageFilter || ''}
            onChange={(e) => setStageFilter(e.target.value)}
            className="input text-sm mt-2"
          >
            <option value="">All Stages</option>
            {pipelineStages.map((stage) => (
              <option key={stage.key} value={stage.key}>
                {stage.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Search */}
      <div className="px-4 md:px-3 py-2">
        <div className="relative">
          <FaSearch
            size={12}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint"
          />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search contacts..."
            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg bg-surface-bg border border-surface-border text-ink-primary placeholder:text-ink-faint outline-none focus:border-brand/40 focus:bg-white transition-colors"
          />
        </div>
      </div>

      {/* Contact list */}
      <div className="overflow-y-auto flex-1">
        {contacts.length === 0 ? (
          <p className="px-4 md:px-3 py-4 text-sm text-ink-muted">No contacts found.</p>
        ) : (
          contacts.map((contact) => {
            const displayName = contact.name || getDisplayName(contact.jid) || formatPhone(contact.phone) || contact.phone;
            const initials = (displayName || '?').replace(/^\+/, '').substring(0, 2).toUpperCase();
            const isGroup = contact.jid?.endsWith('@g.us');
            const stage = pipelineStages.find(s => s.key === contact.stage);
            const temperature = getLeadTemperature(contact.leadScore || 0);

            return (
            <div
              key={contact.phone || contact.jid}
              onClick={() => onSelect(contact)}
              className={`px-4 md:px-3 py-2.5 cursor-pointer border-b border-surface-border transition-colors ${
                selectedContact?.phone === contact.phone
                  ? 'bg-brand-50'
                  : 'hover:bg-surface-bg'
              }`}
            >
              <div className="flex gap-2.5 items-center">
                {/* Avatar */}
                <div className="w-9 h-9 rounded-full flex-shrink-0 overflow-hidden bg-surface-bg border border-surface-border flex items-center justify-center">
                  {contact.avatarUrl ? (
                    <img src={contact.avatarUrl} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                  ) : null}
                  <span className={`text-xs font-semibold text-ink-faint ${contact.avatarUrl ? 'hidden' : 'flex'} items-center justify-center w-full h-full`}>
                    {isGroup ? <FaUsers size={14} className="text-ink-muted" /> : initials}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {contact.isLead && (
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: pipelineStages.find(s => s.key === contact.stage)?.color || '#9CA3AF' }}
                        />
                      )}
                      <span className={`text-sm truncate ${contact.unreadCount > 0 ? 'font-semibold text-ink-primary' : 'font-medium text-ink-primary'}`}>
                        {displayName}
                      </span>
                    </div>
                    {contact.lastTime && (
                      <span className={`text-2xs flex-shrink-0 ${contact.unreadCount > 0 ? 'text-emerald-600 font-semibold' : 'text-ink-faint'}`}>
                        {formatRelativeTime(contact.lastTime)}
                      </span>
                    )}
                  </div>
                  {contact.isLead && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px] font-medium text-white"
                        style={{ backgroundColor: stage?.color || '#9CA3AF' }}
                      >
                        {stage?.label || contact.stage || 'Lead'}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        temperature.key === 'hot'
                          ? 'bg-red-50 text-red-600'
                          : temperature.key === 'warm'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                      }`}>
                        {temperature.label} {contact.leadScore || 0}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center gap-2 mt-0.5">
                    <p className={`text-xs truncate ${contact.unreadCount > 0 ? 'text-ink-primary font-medium' : 'text-ink-muted'}`}>
                      {contact.isChat
                        ? (contact.lastMessage || '')
                        : (contact.isLead ? 'No messages yet' : '')}
                    </p>
                    {contact.unreadCount > 0 && (
                      <span className="bg-emerald-500 text-white text-2xs font-semibold rounded-full min-w-[18px] h-[18px] px-1.5 flex items-center justify-center flex-shrink-0">
                        {contact.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            );
          })
        )}
      </div>
    </div>
  );
}
